import React, { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { ShieldCheck, Users2, CreditCard, Building2, HeartHandshake, Clock, Flag, LogOut, LayoutDashboard, UserPlus, X, ChevronRight, ChevronDown, Moon, Sun, ShoppingBag, Radio, Trash2, CheckCircle2, AlertCircle, RefreshCw, Rocket, Bell, Lock, Menu, BarChart3, FileText, Search, ShieldOff, Ban } from "lucide-react";
import { toast } from "sonner";
import { ccInput, ccLabel, ccCard, ccBtnPink, Avatar, Stars } from "./care/ui";
import MasterAnalytics from "./MasterAnalytics";
import MasterOnboarding from "./MasterOnboarding";
import MasterRecordReview from "./MasterRecordReview";
import MasterHierarchy from "./MasterHierarchy";
import SupportInquiries from "./SupportInquiries";
import StoreAdmin from "./store/StoreAdmin";
import { PlatformAlerts, ComplaintsOversight, BroadcastCenter } from "./MasterAlerts";
import MasterAds from "./MasterAds";
import CommLog from "./CommLog";
import UserDirectory from "./UserDirectory";
import MasterFeatured from "./MasterFeatured";
import SiteContentEditor from "./SiteContentEditor";
import { LiveViewer } from "./modules/LiveEvents";
import { SiteSearch } from "./SiteSearch";

const API = process.env.REACT_APP_BACKEND_URL;

// Clickable stat card detail modal — live data only, no demo records
const StatDetailModal = ({ detail, onClose, onSelect, stats, facilities }) => {
  const [caregivers, setCaregivers] = useState(null);
  useEffect(() => {
    if (!detail) return;
    setCaregivers(null);
    fetch(`${API}/api/directory/users?role=caregiver`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : { users: [] }))
      .then((d) => setCaregivers(d.users || []))
      .catch(() => setCaregivers([]));
  }, [detail]);
  if (!detail) return null;
  const cgList = caregivers || [];
  const cgRows = (list) => list.map((c) => ({ label: c.name || c.email, sub: `${(c.role || "").replace("_", " ")} · ${c.status || "active"}` }));
  const content = {
    facilities: {
      title: "Facilities",
      items: (facilities || []).map((f) => ({ label: f.name, sub: f.id })),
    },
    caregivers: {
      title: "Active Caregivers",
      items: cgRows(cgList.filter((c) => (c.status || "active") === "active")),
    },
    mrr: {
      title: "Monthly Recurring Revenue",
      items: [
        { label: "Subscription revenue", sub: `$${stats?.monthlyRecurringRevenue ?? 0}/mo` },
        { label: "Store revenue (all time)", sub: `$${Number(stats?.storeRevenue ?? 0).toFixed(2)}` },
      ],
    },
    lifetime: {
      title: "Lifetime Caregiver Profiles",
      items: cgRows(cgList),
    },
    pending: {
      title: "Pending Verifications",
      items: cgRows(cgList.filter((c) => c.status === "invited")),
    },
    reported: {
      title: "Reported Profiles",
      items: [{ label: "Open complaints", sub: `${stats?.reportedProfiles ?? 0} awaiting review` }],
    },
    storeOrders: {
      title: "Recent Store Orders",
      items: (stats?.recentStoreOrders || []).map((o) => ({ label: `Order ${o.id}`, sub: `${o.email} · $${o.total} · ${(o.status || "").replace(/_/g, " ")}` })),
    },
    storeRevenue: {
      title: "Store Revenue",
      items: [
        { label: "Paid orders", sub: `${(stats?.recentStoreOrders || []).filter((o) => o.status === "paid").length} of ${stats?.storeOrders ?? 0} total` },
        { label: "Revenue collected", sub: `$${Number(stats?.storeRevenue ?? 0).toFixed(2)}` },
      ],
    },
  }[detail];

  return (
    <div className="fixed inset-0 z-50 bg-[#374151]/30 backdrop-blur-sm flex items-center justify-center p-4 animate-overlay-in" data-testid="stat-detail-modal" onClick={onClose}>
      <div className="bg-white border border-[#FCE6E9] rounded-[2rem] max-w-md w-full p-7 shadow-xl animate-modal-in max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-4 border-b border-[#FCE6E9] pb-3">
          <h3 className="text-base font-extrabold font-heading text-[#374151]">{content.title}</h3>
          <button onClick={onClose} data-testid="stat-detail-close" className="w-8 h-8 rounded-full flex items-center justify-center text-[#9CA3AF] hover:bg-[#FCE6E9] hover:text-[#374151] transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>
        {content.items.length === 0 ? (
          <p className="text-xs text-[#9CA3AF] font-medium py-3 text-center">Nothing here right now.</p>
        ) : (
          <div className="space-y-2">
            {content.items.map((item, i) => {
              const rowCls = "p-3 bg-[#FFF9F2] border border-[#FCE6E9] rounded-xl flex justify-between items-center gap-3 text-xs w-full text-left";
              const inner = (
                <>
                  <span className="font-extrabold text-[#374151]">{item.label}</span>
                  <span className="font-semibold text-[#6B7280] text-right flex items-center gap-1.5">
                    {item.sub}
                    {item.record && <ChevronRight className="w-3.5 h-3.5 text-[#E5A0BC] shrink-0" />}
                  </span>
                </>
              );
              return item.record ? (
                <button key={item.label} onClick={() => onSelect(item.record)} className={`${rowCls} hover:bg-[#E6F2FA]/40 hover:border-[#B3E0F2] cursor-pointer transition-colors`} data-testid={`stat-detail-row-${i}`}>
                  {inner}
                </button>
              ) : (
                <div key={item.label} className={rowCls} data-testid={`stat-detail-row-${i}`}>
                  {inner}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

// ---------- Mock-data purge (go-live) ----------
const PURGE_LABELS = {
  mock_accounts: "Mock user accounts (incl. test admins)",
  facilities_swept: "Demo facilities wiped",
  workspace_docs: "Workspace records (kids, staff, rooms, invoices…)",
  facility_records: "Test facility & onboarding application records",
  store_products: "Test store products",
  store_transactions: "All test orders, payments & carts",
  broadcasts: "Test broadcasts",
  ads: "Test sponsor ads",
  complaints: "Test complaints",
  live_events: "Test live events",
  positions: "Test job positions",
  chat_threads: "Test chat threads",
  inquiries: "Test contact & enrollment inquiries",
  punch_pins: "Test punch PINs",
  billing_invoices: "ALL billing invoices on every facility (full reset to 0)",
  comm_log: "Communications log (all recorded deliveries)",
  admin_inbox: "Test secure-inbox notes & threads",
  analytics_rows: "Site analytics, visits & alert logs (full reset)",
};

const MasterBell = ({ onOpenInbox }) => {
  const [open, setOpen] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [complaints, setComplaints] = useState([]);
  const [applications, setApplications] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [msgAlerts, setMsgAlerts] = useState([]);
  const ref = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/master/review-queue`, { credentials: "include" });
      if (r.ok) {
        const q = await r.json();
        setAlerts(q.alerts || []);
        setComplaints(q.complaints || []);
        setApplications(q.applications || []);
        setDocuments(q.documents || []);
      }
      const b = await fetch(`${API}/api/master/bell`, { credentials: "include" });
      if (b.ok) setMsgAlerts((await b.json()).messages || []);
    } catch { /* keep previous state */ }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => { clearInterval(t); document.removeEventListener("mousedown", onDoc); };
  }, [load]);

  const count = alerts.length + complaints.length + applications.length + documents.length + msgAlerts.length;

  const dismiss = async (aid) => {
    setAlerts((prev) => prev.map((a) => (a.id === aid ? { ...a, read: true } : a)));
    try {
      const res = await fetch(`${API}/api/master/alerts/${aid}/dismiss`, { method: "POST", credentials: "include" });
      if (!res.ok) throw new Error();
    } catch {
      setAlerts((prev) => prev.map((a) => (a.id === aid ? { ...a, read: false } : a)));
      toast.error("Could not dismiss alert — try again.");
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button onClick={() => { const next = !open; setOpen(next); if (next) load(); }} data-testid="master-bell-btn" title="Review queue" className="w-9 h-9 rounded-full bg-white border border-[#FCE6E9] flex items-center justify-center text-[#9CA3AF] hover:text-[#E5A0BC] hover:border-[#F8C8DC] transition-colors relative">
        <Bell className="w-4 h-4" />
        {count > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-400 text-white text-[10px] font-black flex items-center justify-center ring-2 ring-white" data-testid="master-bell-count">
            {count}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-80 bg-white border border-[#FCE6E9] rounded-3xl shadow-xl shadow-blue-900/5 p-4 z-50 animate-modal-in" data-testid="master-bell-dropdown">
          <p className="text-xs font-extrabold uppercase tracking-wider text-[#9CA3AF] font-heading mb-3 border-b border-[#FCE6E9] pb-2">
            Review Queue ({count})
          </p>
          <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
            {count === 0 ? (
              <p className="text-xs text-[#9CA3AF] py-3 text-center" data-testid="master-bell-empty">Nothing waiting for review.</p>
            ) : (
              <>
                {msgAlerts.length > 0 && (
                  <div data-testid="master-bell-messages">
                    <p className="text-[10px] font-extrabold uppercase tracking-wider text-[#E5A0BC] mb-1.5">Messages waiting for staff</p>
                    <div className="space-y-1.5 mb-2">
                      {msgAlerts.map((m) => (
                        <button
                          key={m.id}
                          onClick={() => {
                            setOpen(false);
                            if (m.kind === "secure" && onOpenInbox) onOpenInbox();
                            else window.location.assign("/");
                          }}
                          data-testid={`master-bell-msg-${m.id}`}
                          className="w-full text-left p-2.5 bg-[#E6F2FA] border border-[#B3E0F2] rounded-2xl hover:bg-[#D8ECF7] transition-colors"
                        >
                          <p className="text-[11px] font-extrabold text-[#374151]">
                            {m.kind === "secure" ? "Secure Inbox" : "Chat"} · {m.title}
                          </p>
                          {m.body && <p className="text-[10px] text-[#6B7280] font-medium line-clamp-1">{m.body}</p>}
                          <p className="text-[9px] font-extrabold text-[#3D7E9E] mt-0.5">Open &amp; respond →</p>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {complaints.map((c) => (
                  <div key={`c_${c.id}`} className="p-3 bg-[#FFF2D1]/60 border border-[#F5E3B3] rounded-2xl text-xs" data-testid={`master-bell-complaint-${c.id}`}>
                    <p className="font-extrabold text-[#A08244] text-[10px] uppercase tracking-wide mb-0.5">Complaint · {c.facilityName || "Facility"}</p>
                    <p className="text-[#374151] font-medium">{c.subject}</p>
                  </div>
                ))}
                {applications.map((a) => (
                  <div key={`a_${a.id}`} className="p-3 bg-[#E6F2FA]/60 border border-[#B3E0F2] rounded-2xl text-xs" data-testid={`master-bell-application-${a.id}`}>
                    <p className="font-extrabold text-[#3D7E9E] text-[10px] uppercase tracking-wide mb-0.5">Facility application</p>
                    <p className="text-[#374151] font-medium">{a.name}{a.detail ? ` · ${a.detail}` : ""}</p>
                  </div>
                ))}
                {documents.map((d) => (
                  <div key={`d_${d.id}`} className="p-3 bg-[#E2F4E1]/60 border border-[#C4E5C2] rounded-2xl text-xs" data-testid={`master-bell-document-${d.id}`}>
                    <p className="font-extrabold text-[#2F6031] text-[10px] uppercase tracking-wide mb-0.5">Document to review · {(d.category || "").replace(/_/g, " ")}</p>
                    <p className="text-[#374151] font-medium">{d.title}</p>
                  </div>
                ))}
                {alerts.map((a) => (
                  <div key={a.id} className="p-3 bg-[#FFF9F2] border border-[#FCE6E9] rounded-2xl flex justify-between items-start text-xs gap-2" data-testid={`master-bell-alert-${a.id}`}>
                    <p className="text-[#374151] font-medium">{a.message}</p>
                    <button onClick={() => dismiss(a.id)} data-testid={`master-bell-dismiss-${a.id}`} className="text-[#C9CDD4] hover:text-[#6B7280] transition-colors shrink-0">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const PurgeMockButton = () => {
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);

  const openModal = async () => {
    setOpen(true);
    setPreview(null);
    setConfirmText("");
    try {
      const res = await fetch(`${API}/api/master/purge-mock/preview`, { credentials: "include" });
      const body = await res.json();
      if (!res.ok) throw new Error(typeof body.detail === "string" ? body.detail : "Preview failed");
      setPreview(body);
    } catch (err) {
      toast.error(err.message);
      setOpen(false);
    }
  };

  const purge = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/master/purge-mock`, { method: "POST", credentials: "include" });
      const body = await res.json();
      if (!res.ok) throw new Error(typeof body.detail === "string" ? body.detail : "Purge failed");
      toast.success(`Mock data removed — ${body.purged?.mock_accounts ?? 0} accounts, ${body.purged?.workspace_docs ?? 0} workspace records purged. All invoices, payments & analytics reset to zero.`);
      setOpen(false);
      window.location.reload();
    } catch (err) {
      toast.error(err.message);
      setBusy(false);
    }
  };

  const total = preview ? Object.values(preview).reduce((a, b) => a + b, 0) : 0;

  return (
    <>
      <button
        onClick={openModal}
        data-testid="purge-mock-btn"
        title="Remove all demo/test data and reset billing, invoices & analytics to zero — real accounts and facilities are kept"
        className="hidden sm:flex items-center gap-1.5 px-4 py-2 bg-white hover:bg-rose-50 border border-rose-200 rounded-full text-xs font-bold text-rose-500 transition-all shadow-sm"
      >
        <Trash2 className="w-3.5 h-3.5" /> Purge Mock Data
      </button>
      {open && createPortal(
        <div className="fixed inset-0 z-[100] bg-[#374151]/40 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto animate-overlay-in" data-testid="purge-mock-modal" onClick={() => !busy && setOpen(false)}>
          <div className="bg-white border border-[#FCE6E9] rounded-[2rem] max-w-md w-full p-6 shadow-xl animate-modal-in max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-lg font-extrabold font-heading text-[#374151]">Purge Mock Data</h3>
              <button onClick={() => setOpen(false)} data-testid="purge-mock-close" className="w-8 h-8 rounded-full flex items-center justify-center text-[#9CA3AF] hover:bg-[#FCE6E9] hover:text-[#374151] transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>
            <p className="text-xs text-[#6B7280] font-medium mb-4">
              Removes demo/test data — all accounts on @example.com & @resend.dev (including test admins), TEST-* items, demo facilities with no real users, all test orders/payments, <b>ALL billing invoices on every facility</b>, and site analytics so every counter resets to zero. Your real accounts, real facilities, curated store products, and admin-sent invites are never touched.
            </p>
            {preview === null ? (
              <p className="text-xs text-[#9CA3AF] font-medium py-4 text-center" data-testid="purge-preview-loading">Scanning for mock data…</p>
            ) : (
              <>
                <div className="space-y-1 mb-3 max-h-36 overflow-y-auto" data-testid="purge-preview-list">
                  {Object.entries(preview).map(([k, v]) => (
                    <div key={k} className="flex items-center justify-between p-2.5 bg-[#FFF9F2] border border-[#FCE6E9] rounded-xl">
                      <span className="text-[11px] font-bold text-[#6B7280]">{PURGE_LABELS[k] || k}</span>
                      <span className={`text-xs font-extrabold ${v > 0 ? "text-rose-500" : "text-[#9CA3AF]"}`}>{v}</span>
                    </div>
                  ))}
                </div>
                <p className="text-[11px] font-bold text-[#374151] mb-2" data-testid="purge-total">Total to remove: {total} items</p>
                <input
                  type="text"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder="Type PURGE to confirm"
                  className={`${ccInput} mb-3`}
                  data-testid="purge-confirm-input"
                />
                <button
                  onClick={purge}
                  disabled={confirmText !== "PURGE" || busy || total === 0}
                  data-testid="purge-confirm-btn"
                  className="w-full px-4 py-3 bg-rose-500 hover:bg-rose-600 text-white rounded-full text-sm font-extrabold transition-colors disabled:opacity-40"
                >
                  {busy ? "Purging…" : "Remove All Mock Data"}
                </button>
              </>
            )}
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

const GoLiveChecklist = () => {
  const [checklist, setChecklist] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadChecklist = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/master/go-live`, { credentials: "include" });
      if (res.ok) setChecklist(await res.json());
    } catch { /* keep previous state */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadChecklist(); }, [loadChecklist]);

  if (!checklist) return null;

  const done = checklist.checks.filter((c) => c.ok).length;
  return (
    <div className={`${ccCard} p-6 animate-fade-up`} data-testid="go-live-checklist">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base font-extrabold font-heading flex items-center gap-2.5">
          <span className="w-9 h-9 rounded-full bg-[#E2F4E1] flex items-center justify-center">
            <Rocket className="w-4 h-4 text-[#2F6031]" />
          </span>
          Go-Live Checklist
        </h3>
        <div className="flex items-center gap-2">
          <span
            className={`px-2.5 py-1 text-[10px] font-extrabold uppercase rounded-full border ${checklist.ready ? "bg-[#E2F4E1] border-[#B7E3B4] text-[#2F6031]" : "bg-[#FFF2D1] border-[#F5E3B3] text-[#A08244]"}`}
            data-testid="go-live-status"
          >
            {checklist.ready ? "Ready to launch" : `${done}/${checklist.checks.length} done`}
          </span>
          <button onClick={loadChecklist} disabled={loading} data-testid="go-live-refresh-btn" title="Re-check readiness" className="w-8 h-8 rounded-full flex items-center justify-center text-[#9CA3AF] hover:bg-[#E6F2FA] hover:text-[#374151] transition-colors disabled:opacity-40">
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2.5">
        {checklist.checks.map((c) => (
          <div key={c.key} className="p-3 bg-[#FFF9F2] border border-[#FCE6E9] rounded-2xl" data-testid={`go-live-check-${c.key}`}>
            <div className="flex items-center gap-1.5 mb-1">
              {c.ok ? <CheckCircle2 className="w-3.5 h-3.5 text-[#2F6031] shrink-0" /> : <AlertCircle className="w-3.5 h-3.5 text-[#D97706] shrink-0" />}
              <p className="text-[11px] font-extrabold text-[#374151] leading-tight">{c.label}</p>
            </div>
            <p className="text-[10px] font-medium text-[#9CA3AF] leading-snug">{c.detail}</p>
          </div>
        ))}
      </div>
    </div>
  );
};


const POSITIONS = [
  { value: "secretary", label: "Secretary" },
  { value: "cfo", label: "CFO" },
  { value: "accountant", label: "Accountant" },
  { value: "admin", label: "Admin" },
];

// ---------- Collapsible section menu (dashboard navigation) ----------
const DASH_MENU = [
  { label: "Overview", icon: LayoutDashboard, items: [
    { label: "Platform Stats", target: "master-stats" },
    { label: "Go-Live Checklist", target: "sec-golive" },
  ]},
  { label: "Operations", icon: Building2, items: [
    { label: "Daycare Operations", target: "__daycare-ops__" },
    { label: "Community Live (Public)", target: "__community-live__" },
  ]},
  { label: "People", icon: Users2, items: [
    { label: "User Directory", target: "user-directory" },
    { label: "Invite Master Staff", target: "master-invite-panel" },
    { label: "Master Staff", target: "master-staff-panel" },
  ]},
  { label: "Onboarding", icon: UserPlus, items: [
    { label: "Applications & Enrollment", target: "sec-onboarding" },
    { label: "Hierarchy & Positions", target: "sec-hierarchy" },
  ]},
  { label: "Support & Safety", icon: Flag, items: [
    { label: "Support Inquiries", target: "sec-support" },
    { label: "Platform Alerts", target: "sec-alerts" },
    { label: "Complaints", target: "sec-complaints" },
  ]},
  { label: "Communications", icon: Radio, items: [
    { label: "Broadcast Center", target: "sec-broadcast" },
    { label: "Communications Log", target: "sec-commlog" },
  ]},
  { label: "Monetization", icon: ShoppingBag, items: [
    { label: "Store Manager", target: "sec-store" },
    { label: "Sponsor Ads", target: "sec-ads" },
    { label: "Featured Daycares", target: "featured-panel" },
  ]},
  { label: "Content", icon: FileText, items: [
    { label: "Site Content & SEO", target: "site-content-panel" },
    { label: "State Provider Directory", target: "__directory__", href: "/providers" },
  ]},
  { label: "Analytics", icon: BarChart3, items: [
    { label: "Analytics & Activity", target: "sec-analytics" },
  ]},
];

const scrollToTarget = (target) => {
  if (target === "__directory__") {
    window.location.assign("/providers");
    return;
  }
  const el = document.getElementById(target) || document.querySelector(`[data-testid="${target}"]`);
  if (!el) return;
  if (target === "user-directory") window.dispatchEvent(new Event("nt-open-directory"));
  el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const DashNav = ({ collapsed, active, onNav, badges = {}, onExpand }) => {
  const [openGroups, setOpenGroups] = useState(() => Object.fromEntries(DASH_MENU.map((g) => [g.label, true])));
  const [navQuery, setNavQuery] = useState("");
  const navMatches = navQuery.trim()
    ? DASH_MENU.flatMap((g) => g.items.map((it) => ({ ...it, group: g.label }))).filter((it) => `${it.label} ${it.group}`.toLowerCase().includes(navQuery.trim().toLowerCase()))
    : [];
  return (
    <nav className="space-y-1" data-testid="master-dash-nav">
      {!collapsed && (
        <div className="relative mb-2">
          <Search className="w-3.5 h-3.5 text-[#9CA3AF] absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input
            value={navQuery}
            onChange={(e) => setNavQuery(e.target.value)}
            placeholder="Search menu…"
            className="w-full bg-[#FFF9F2] border border-[#FCE6E9] rounded-full pl-8 pr-3 py-2 text-xs font-semibold outline-none focus:border-[#E5A0BC] placeholder:text-[#C9B7C0]"
            data-testid="dash-nav-search"
          />
        </div>
      )}
      {navQuery.trim() && (
        <div className="mb-2 bg-[#FFF9F2] border border-[#FCE6E9] rounded-2xl p-1.5 space-y-0.5" data-testid="dash-nav-results">
          {navMatches.map((it) => (
            <a
              key={it.target}
              href={it.href || `#${it.target}`}
              onClick={(e) => {
                e.preventDefault();
                setNavQuery("");
                onNav(it);
              }}
              data-testid={`dash-nav-result-${it.target}`}
              className="flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-xl hover:bg-white text-xs font-bold text-[#374151] !no-underline transition-colors"
            >
              <span>{it.label}</span>
              <span className="text-[9px] font-extrabold text-[#9CA3AF] uppercase shrink-0">{it.group}</span>
            </a>
          ))}
          {navMatches.length === 0 && (
            <p className="text-[10px] text-[#9CA3AF] font-semibold px-2.5 py-1.5" data-testid="dash-nav-no-results">No menu items match — try "ads", "store", "directory"…</p>
          )}
        </div>
      )}
      {!navQuery.trim() && DASH_MENU.map((group) => {
        const groupCount = group.items.reduce((n, item) => n + (badges[item.target] || 0), 0);
        return (
        <div key={group.label}>
          <button
            onClick={() => {
              // Collapsed icon rail: tapping an icon expands the menu straight into that section's sub-pages
              if (collapsed && onExpand) {
                setOpenGroups((o) => ({ ...o, [group.label]: true }));
                onExpand(group);
                return;
              }
              setOpenGroups((o) => ({ ...o, [group.label]: !o[group.label] }));
            }}
            data-testid={`dash-nav-group-${group.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            title={group.label}
            className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl hover:bg-[#FCE6E9] text-xs font-extrabold text-[#374151] transition-colors"
          >
            <span className="relative shrink-0">
              <group.icon className="w-4 h-4 text-[#E5A0BC]" />
              {collapsed && groupCount > 0 && <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-rose-500 rounded-full" data-testid={`dash-nav-group-dot-${group.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} />}
            </span>
            {!collapsed && (
              <>
                <span className="flex-1 text-left">{group.label}</span>
                {groupCount > 0 && (
                  <span className="px-1.5 py-0.5 bg-rose-500 text-white text-[9px] font-black rounded-full" data-testid={`dash-nav-group-badge-${group.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                    {groupCount}
                  </span>
                )}
                <ChevronDown className={`w-3.5 h-3.5 text-[#9CA3AF] transition-transform ${openGroups[group.label] ? "" : "-rotate-90"}`} />
              </>
            )}
          </button>
          {openGroups[group.label] && !collapsed && (
            <div className="ml-5 pl-3 border-l-2 border-[#FCE6E9] space-y-0.5 mt-0.5 mb-1.5">
              {group.items.map((item) => (
                <button
                  key={item.target}
                  onClick={() => onNav(item)}
                  data-testid={`dash-nav-item-${item.target}`}
                  className={`w-full flex items-center gap-2 text-left px-3 py-1.5 rounded-lg text-[11px] font-bold transition-colors ${active === item.target ? "bg-[#F8C8DC] text-[#374151]" : "text-[#6B7280] hover:bg-[#FFF9F2] hover:text-[#374151]"}`}
                >
                  <span className="flex-1">{item.label}</span>
                  {Boolean(badges[item.target]) && (
                    <span className="px-1.5 py-0.5 bg-rose-500 text-white text-[9px] font-black rounded-full" data-testid={`dash-nav-badge-${item.target}`}>
                      {badges[item.target]}
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
        );
      })}
    </nav>
  );
};

export default function MasterDash({ currentUser, onSignOut, onDashboard, onHome, onEditAvatar, onOpenOwnerModal, onOpenEnrollModal, onOpenInbox, theme, onToggleTheme, onSiteNav }) {
  const [navOpen, setNavOpen] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activeNav, setActiveNav] = useState("master-stats");
  const [communityLiveOpen, setCommunityLiveOpen] = useState(false);
  const [clForm, setClForm] = useState({ title: "", provider: "jitsi" });
  const [clBusy, setClBusy] = useState(false);
  const [clEvent, setClEvent] = useState(null);
  const startCommunityLive = async (e) => {
    e.preventDefault();
    setClBusy(true);
    try {
      const res = await fetch(`${API}/api/live/start`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ title: clForm.title, description: "", is_public: true, provider: clForm.provider }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof body.detail === "string" ? body.detail : "Could not start the event");
      setClEvent(body);
      setCommunityLiveOpen(false);
      setClForm((f) => ({ ...f, title: "" }));
      toast.success("You're live — the event is on the public homepage now. Share link copied to your clipboard.");
      navigator.clipboard?.writeText(`${window.location.origin}/api/share/live/${body.id}`).catch(() => {});
    } catch (err) {
      toast.error(err.message);
    } finally {
      setClBusy(false);
    }
  };

  // Live Control — searchable site-wide list of active streams with one-tap watch/end/delete
  const [liveCtlOpen, setLiveCtlOpen] = useState(false);
  const [liveCtlList, setLiveCtlList] = useState([]);
  const [liveCtlQ, setLiveCtlQ] = useState("");
  const [liveCtlBusy, setLiveCtlBusy] = useState(null);
  const [liveCtlWatch, setLiveCtlWatch] = useState(null);
  const loadLiveCtl = useCallback(() => {
    fetch(`${API}/api/live/active`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => setLiveCtlList(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, []);
  useEffect(() => {
    loadLiveCtl();
    const iv = setInterval(loadLiveCtl, liveCtlOpen ? 3000 : 15000);
    return () => clearInterval(iv);
  }, [liveCtlOpen, loadLiveCtl]);
  const endLiveCtl = async (id) => {
    setLiveCtlBusy(id);
    try {
      const res = await fetch(`${API}/api/live/${id}/end`, { method: "POST", credentials: "include" });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof body.detail === "string" ? body.detail : "Could not end live");
      toast.success("Live event ended — its broadcast was retired too.");
      loadLiveCtl();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLiveCtlBusy(null);
    }
  };
  const deleteLiveCtl = async (id, title) => {
    if (!window.confirm(`Delete "${title}" permanently? This removes the event and any replay.`)) return;
    setLiveCtlBusy(id);
    try {
      const res = await fetch(`${API}/api/live/${id}`, { method: "DELETE", credentials: "include" });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof body.detail === "string" ? body.detail : "Could not delete");
      toast.success("Live event deleted.");
      loadLiveCtl();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLiveCtlBusy(null);
    }
  };
  const liveCtlFiltered = liveCtlList.filter((ev) => `${ev.title} ${ev.host_name} ${ev.facilityName || ""}`.toLowerCase().includes(liveCtlQ.trim().toLowerCase()));
  const banLiveHost = async (email, name) => {
    if (!window.confirm(`Ban ${name || email} from hosting live events? All their active streams end immediately.`)) return;
    setLiveCtlBusy("ban");
    try {
      const res = await fetch(`${API}/api/master/live-ban`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, banned: true }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof body.detail === "string" ? body.detail : "Could not ban host");
      toast.success(`${name || email} banned from live hosting — ${body.ended_streams || 0} stream(s) ended.`);
      loadLiveCtl();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLiveCtlBusy(null);
    }
  };
  const [navBadges, setNavBadges] = useState({});
  useEffect(() => {
    const loadBadges = () =>
      fetch(`${API}/api/master/nav-badges`, { credentials: "include" })
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => d && setNavBadges(d.badges || {}))
        .catch(() => {});
    loadBadges();
    const iv = setInterval(loadBadges, 30000);
    return () => clearInterval(iv);
  }, []);
  const [data, setData] = useState(null);
  const [customPositions, setCustomPositions] = useState([]);

  const loadPositions = useCallback(() => {
    fetch(`${API}/api/master/positions`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : { positions: [] }))
      .then((d) => setCustomPositions(d.positions || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    loadPositions();
    window.addEventListener("nt-positions-changed", loadPositions);
    return () => window.removeEventListener("nt-positions-changed", loadPositions);
  }, [loadPositions]);
  const [form, setForm] = useState({ name: "", email: "", position: "secretary", region: "", role: "master" });
  const [inviting, setInviting] = useState(false);
  const [statDetail, setStatDetail] = useState(null);
  const [recordReview, setRecordReview] = useState(null);

  const load = async () => {
    try {
      const res = await fetch(`${API}/api/master/overview`, { credentials: "include" });
      if (!res.ok) throw new Error("unauthorized");
      setData(await res.json());
    } catch {
      toast.error("Master overview unavailable.");
    }
  };

  useEffect(() => {
    load();
  }, []);

  const [lastInvite, setLastInvite] = useState(null);

  const invite = async (e) => {
    e.preventDefault();
    setInviting(true);
    try {
      let res = null;
      let body = {};
      for (let attempt = 1; attempt <= 2; attempt++) {
        res = await fetch(`${API}/api/master/invite`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify(form),
        });
        body = await res.json().catch(() => ({})); // gateway HTML error pages (502/503 during deploys) are not JSON
        if (res.status < 500) break;
        if (attempt === 1) {
          toast.info("Server is restarting — retrying…");
          await new Promise((r) => setTimeout(r, 3000));
        }
      }
      if (!res.ok) throw new Error(typeof body.detail === "string" ? body.detail : `Invite failed (server ${res.status}) — if a publish is running, try again in a minute`);
      const roleLabel = { master: form.position, caregiver: "Caregiver", daycare_admin: "Daycare Owner", parent: "Parent" }[form.role] || form.role;
      setLastInvite({ name: form.name, email: form.email, role: form.role, position: roleLabel, temp: body.temp_password, link: body.claim_link, emailSent: body.email_sent !== false });
      toast.success(body.resent ? `${form.email}'s invite was re-sent with a fresh link and temporary password.` : `${form.name} invited as ${roleLabel} — invite email sent.`);
      setForm({ ...form, name: "", email: "" });
      load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setInviting(false);
    }
  };

  const [siteStatus, setSiteStatus] = useState(null);
  useEffect(() => {
    fetch(`${API}/api/site/status`)
      .then((r) => (r.ok ? r.json() : null))
      .then(setSiteStatus)
      .catch(() => {});
  }, []);

  const stats = data?.stats;
  // Position-based permission controls: custom job positions grant feature access (default allow for legacy owner/admin)
  const canDo = (perm) => !currentUser?.permissions?.length || currentUser.permissions.includes(perm);
  const cards = stats
    ? [
        { label: "Facilities", value: stats.totalFacilities, icon: Building2, wrap: "bg-[#E6F2FA]", color: "text-[#5CB3DC]", testId: "master-stat-facilities", detail: "facilities" },
        { label: "Active Caregivers", value: stats.activeCaregivers, icon: HeartHandshake, wrap: "bg-[#FCE6E9]", color: "text-[#E5A0BC]", testId: "master-stat-caregivers", detail: "caregivers" },
        { label: "MRR", value: `$${stats.monthlyRecurringRevenue}`, icon: CreditCard, wrap: "bg-[#E2F4E1]", color: "text-[#7CC98F]", testId: "master-stat-mrr", detail: "mrr" },
        { label: "Lifetime Profiles", value: stats.lifetimeCaregiverProfiles, icon: Users2, wrap: "bg-[#FFF2D1]", color: "text-[#D9A93E]", testId: "master-stat-lifetime", detail: "lifetime" },
        { label: "Pending Verifications", value: stats.pendingVerifications, icon: Clock, wrap: "bg-[#FFF2D1]", color: "text-[#D9A93E]", testId: "master-stat-pending", detail: "pending" },
        { label: "Reported", value: stats.reportedProfiles, icon: Flag, wrap: "bg-rose-50", color: "text-rose-400", testId: "master-stat-reported", detail: "reported" },
        { label: "Store Orders", value: stats.storeOrders ?? 0, icon: ShoppingBag, wrap: "bg-[#E6F2FA]", color: "text-[#5CB3DC]", testId: "master-stat-store-orders", detail: "storeOrders" },
        { label: "Store Revenue", value: `$${Number(stats.storeRevenue ?? 0).toFixed(0)}`, icon: CreditCard, wrap: "bg-[#E2F4E1]", color: "text-[#2F6031]", testId: "master-stat-store-revenue", detail: "storeRevenue" },
      ]
    : [];

  return (
    <div className="min-h-screen bg-[#FFF8E7] text-[#374151] dark:bg-[#1F2937] dark:text-slate-200" data-testid="master-dash">
      {/* Header */}
      <header className="border-b border-[#FCE6E9] bg-white/80 backdrop-blur-xl px-6 py-3.5 flex items-center justify-between sticky top-0 z-40">
        <button onClick={onHome} className="flex items-center gap-2.5 cursor-pointer" data-testid="master-brand" title="Back to home page">
          <div className="w-10 h-10 rounded-full bg-white border-2 border-[#FCE6E9] shadow-sm overflow-hidden flex items-center justify-center">
            <img src="/assets/logo.png" alt="NestTrack" className="w-full h-full object-cover" />
          </div>
          <div className="text-left">
            <span className="font-black text-xl tracking-tight font-heading">
              Nest<span className="text-[#E5A0BC]">Track</span>
            </span>
            <span className="ml-2 px-2.5 py-0.5 bg-[#374151] text-white text-[9px] font-extrabold uppercase tracking-wider rounded-full" data-testid="master-badge">
              Master Control
            </span>
          </div>
        </button>
        {onSiteNav && (
          <div className="hidden md:block flex-1 max-w-md mx-4 min-w-0">
            <SiteSearch currentUser={currentUser} onNavigate={onSiteNav} testid="master-site-search" placeholder="Search panels & pages…" />
          </div>
        )}
        <div className="flex items-center gap-2.5">
          {(!siteStatus?.live_mode || siteStatus?.upgrade_available) && (
          <button
            onClick={() => { sessionStorage.setItem("nt_view", "dashboard"); sessionStorage.setItem("nt_ops_tab", "live"); onDashboard(); }}
            data-testid="master-golive-btn"
            className="flex items-center gap-1.5 px-3.5 py-2 bg-rose-500 hover:bg-rose-600 text-white rounded-full text-xs font-bold transition-all shadow-sm"
          >
            <Radio className="w-3.5 h-3.5" /> Go Live
          </button>
          )}
          <button
            onClick={onHome}
            data-testid="master-view-site-btn"
            title="View the public landing page (edit site copy inline with the pencil icons)"
            className="hidden sm:flex items-center gap-1.5 px-4 py-2 bg-white hover:bg-[#FCE6E9] border border-[#FCE6E9] rounded-full text-xs font-bold text-[#374151] transition-all shadow-sm"
          >
            <Rocket className="w-3.5 h-3.5 text-[#E5A0BC]" /> View Site
          </button>
          <button
            onClick={() => setDrawerOpen(true)}
            data-testid="dash-nav-open"
            title="Dashboard sections"
            className="lg:hidden w-9 h-9 rounded-full bg-white border border-[#FCE6E9] flex items-center justify-center text-[#374151] hover:bg-[#FCE6E9] transition-colors"
          >
            <Menu className="w-4 h-4" />
          </button>
          <button
            onClick={() => { window.dispatchEvent(new Event("nt-open-directory")); document.getElementById("user-directory")?.scrollIntoView({ behavior: "smooth", block: "start" }); }}
            data-testid="master-contacts-btn"
            className="hidden sm:flex items-center gap-1.5 px-4 py-2 bg-white hover:bg-[#E6F2FA] border border-[#E6F2FA] rounded-full text-xs font-bold text-[#374151] transition-all shadow-sm"
          >
            <Users2 className="w-3.5 h-3.5 text-[#5CB3DC]" /> Contacts
          </button>
          <PurgeMockButton />
          {onOpenInbox && (
            <button onClick={onOpenInbox} data-testid="master-inbox-btn" title="Admin secure inbox — threads, private notes & escalations" className="hidden sm:flex items-center gap-1.5 px-4 py-2 bg-white hover:bg-[#E6F2FA] border border-[#E6F2FA] rounded-full text-xs font-bold text-[#374151] transition-all shadow-sm">
              <Lock className="w-3.5 h-3.5 text-[#5CB3DC]" /> Secure Inbox
            </button>
          )}
          <MasterBell onOpenInbox={onOpenInbox} />
          {onToggleTheme && (
            <button
              onClick={onToggleTheme}
              data-testid="master-theme-toggle"
              title="Toggle theme"
              className="hidden sm:flex w-9 h-9 rounded-full bg-white border border-[#FCE6E9] items-center justify-center text-[#9CA3AF] hover:text-[#E5A0BC] hover:border-[#F8C8DC] transition-colors"
            >
              {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>
          )}
          <select
            value={typeof window !== "undefined" ? sessionStorage.getItem("nt_facility_override") || "" : ""}
            onChange={(e) => {
              if (e.target.value) sessionStorage.setItem("nt_facility_override", e.target.value);
              else sessionStorage.removeItem("nt_facility_override");
              sessionStorage.setItem("nt_view", "dashboard");
              onDashboard();
            }}
            data-testid="master-facility-override"
            title="Operate on a facility's workspace — view & edit their sections on request"
            className="hidden sm:block px-3.5 py-2 bg-white border border-[#E6F2FA] rounded-full text-xs font-bold text-[#374151] shadow-sm cursor-pointer hover:bg-[#E6F2FA] transition-colors"
          >
            <option value="">Operate on facility…</option>
            {(data?.facilities || []).map((f) => (
              <option key={f.id} value={f.id}>{f.name}</option>
            ))}
          </select>
          <button
            onClick={onDashboard}
            data-testid="master-preview-dashboard-btn"
            className="hidden sm:flex items-center gap-1.5 px-4 py-2 bg-white hover:bg-[#E6F2FA] border border-[#E6F2FA] rounded-full text-xs font-bold text-[#374151] transition-all shadow-sm"
          >
            <LayoutDashboard className="w-3.5 h-3.5 text-[#5CB3DC]" /> Daycare Dashboard
          </button>
          <button
            onClick={() => setCommunityLiveOpen(true)}
            data-testid="master-community-live-btn"
            className="flex items-center gap-1.5 px-3.5 py-2 bg-rose-500 hover:bg-rose-600 text-white rounded-full text-xs font-bold transition-all shadow-sm"
          >
            <Radio className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Community</span> Live
          </button>
          <button
            onClick={() => setLiveCtlOpen(true)}
            data-testid="master-live-control-btn"
            title="Live Control — search every active stream site-wide and end or delete it at a touch"
            className="relative flex items-center gap-1.5 px-3.5 py-2 bg-white hover:bg-rose-50 border border-rose-200 text-rose-500 rounded-full text-xs font-bold transition-all shadow-sm dark:bg-[#273244] dark:border-rose-900"
          >
            <ShieldOff className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Live Control</span>
            {liveCtlList.length > 0 && (
              <span className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-rose-500 text-white text-[10px] font-black flex items-center justify-center animate-pulse" data-testid="live-control-count">{liveCtlList.length}</span>
            )}
          </button>
          {communityLiveOpen && createPortal(
            <div className="fixed inset-0 z-[80] bg-black/40 flex items-center justify-center p-4" onClick={() => setCommunityLiveOpen(false)} data-testid="community-live-modal">
              <form onClick={(e) => e.stopPropagation()} onSubmit={startCommunityLive} className="w-full max-w-md bg-white rounded-3xl border-2 border-[#FCE6E9] shadow-2xl p-6 dark:bg-[#273244] dark:border-[#374457]">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-base font-black font-heading text-[#374151] dark:text-slate-100 flex items-center gap-2"><Radio className="w-4 h-4 text-rose-500" /> Start a Community Live Event</h3>
                  <button type="button" onClick={() => setCommunityLiveOpen(false)} className="p-1.5 hover:bg-[#FCE6E9] rounded-full transition-colors" data-testid="community-live-close"><X className="w-4 h-4 text-[#9CA3AF]" /></button>
                </div>
                <p className="text-[11px] text-[#9CA3AF] font-medium mb-3">Public — appears on the NestTrack homepage Live Now strip for everyone.</p>
                <input required type="text" value={clForm.title} onChange={(e) => setClForm({ ...clForm, title: e.target.value })} placeholder="Event title (e.g., Friday Parent Q&A)" className={`${ccInput} mb-3`} data-testid="community-live-title" />
                <div className="flex items-center gap-1.5 mb-4" data-testid="community-live-provider">
                  <span className="text-[10px] font-bold text-[#9CA3AF] uppercase tracking-wide mr-1">Video via</span>
                  {[["jitsi", "Jitsi (open)"], ["livekit", "LiveKit (native)"]].map(([key, label]) => (
                    <button key={key} type="button" onClick={() => setClForm({ ...clForm, provider: key })} data-testid={`community-live-provider-${key}`} className={`px-3 py-1.5 rounded-full text-[10px] font-extrabold transition-all ${clForm.provider === key ? "bg-[#5CB3DC] text-white shadow-sm" : "bg-white border border-[#B3E0F2] text-[#3D7E9E] hover:bg-[#E6F2FA]"}`}>
                      {label}
                    </button>
                  ))}
                </div>
                <button type="submit" disabled={clBusy} className={`${ccBtnPink} w-full disabled:opacity-50`} data-testid="community-live-start">{clBusy ? "Starting…" : "Go Live — Public"}</button>
              </form>
            </div>,
            document.body
          )}
          {liveCtlOpen && createPortal(
            <div className="fixed inset-0 z-[80] bg-black/40 flex items-center justify-center p-4" onClick={() => setLiveCtlOpen(false)} data-testid="live-control-modal">
              <div onClick={(e) => e.stopPropagation()} className="w-full max-w-lg bg-white rounded-3xl border-2 border-[#FCE6E9] shadow-2xl p-6 dark:bg-[#273244] dark:border-[#374457] max-h-[85vh] flex flex-col">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-base font-black font-heading text-[#374151] dark:text-slate-100 flex items-center gap-2"><ShieldOff className="w-4 h-4 text-rose-500" /> Live Control</h3>
                  <button type="button" onClick={() => setLiveCtlOpen(false)} className="p-1.5 hover:bg-[#FCE6E9] rounded-full transition-colors" data-testid="live-control-close"><X className="w-4 h-4 text-[#9CA3AF]" /></button>
                </div>
                <div className="relative mb-4">
                  <Search className="w-3.5 h-3.5 text-[#9CA3AF] absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                  <input value={liveCtlQ} onChange={(e) => setLiveCtlQ(e.target.value)} placeholder="Search live events — title, host, facility…" className={`${ccInput} !pl-9`} data-testid="live-control-search" />
                </div>
                <div className="space-y-3 overflow-y-auto" data-testid="live-control-list">
                  {liveCtlFiltered.length === 0 ? (
                    <p className="text-xs text-[#9CA3AF] font-semibold text-center py-6" data-testid="live-control-empty">
                      {liveCtlList.length === 0 ? "Nothing is live anywhere on NestTrack right now." : "No live events match your search."}
                    </p>
                  ) : (
                    liveCtlFiltered.map((ev) => (
                      <div key={ev.id} className="bg-[#FFF9F2] border border-[#FCE6E9] rounded-2xl p-4 dark:bg-[#232D3D] dark:border-[#374457]" data-testid={`live-control-row-${ev.id}`}>
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-sm font-black text-[#374151] dark:text-slate-100 flex items-center gap-2 min-w-0">
                            <span className="w-2.5 h-2.5 rounded-full bg-rose-500 animate-pulse shrink-0" /> <span className="truncate">{ev.title}</span>
                          </p>
                          <span className={`text-[9px] font-extrabold uppercase px-2 py-0.5 rounded-full shrink-0 ${ev.is_public ? "bg-[#E2F4E1] text-[#2F6031]" : "bg-[#FFF2D1] text-[#8a6d1a]"}`}>{ev.is_public ? "Public" : "Facility"}</span>
                        </div>
                        <p className="text-[11px] text-[#9CA3AF] font-medium mt-1">{ev.host_name} · {ev.facilityName || "NestTrack"} · {ev.provider}</p>
                        <div className="flex gap-2 mt-3 flex-wrap">
                          <button onClick={() => setLiveCtlWatch(ev.id)} className="flex items-center gap-1.5 px-3.5 py-2 bg-[#E6F2FA] hover:bg-[#D2E9F5] border border-[#B3E0F2] text-[#3D7E9E] rounded-full text-[11px] font-bold transition-colors" data-testid={`live-control-watch-${ev.id}`}>
                            <Radio className="w-3.5 h-3.5" /> Watch
                          </button>
                          <button onClick={() => endLiveCtl(ev.id)} disabled={liveCtlBusy === ev.id} className="flex items-center gap-1.5 px-3.5 py-2 bg-rose-500 hover:bg-rose-600 text-white rounded-full text-[11px] font-bold transition-colors disabled:opacity-50" data-testid={`live-control-end-${ev.id}`}>
                            <ShieldOff className="w-3.5 h-3.5" /> {liveCtlBusy === ev.id ? "Working…" : "End Live"}
                          </button>
                          <button onClick={() => deleteLiveCtl(ev.id, ev.title)} disabled={liveCtlBusy === ev.id} className="flex items-center gap-1.5 px-3.5 py-2 bg-rose-50 hover:bg-rose-100 border border-rose-200 text-rose-500 rounded-full text-[11px] font-bold transition-colors disabled:opacity-50" data-testid={`live-control-delete-${ev.id}`}>
                            <Trash2 className="w-3.5 h-3.5" /> Delete
                          </button>
                          <button
                            onClick={() => banLiveHost(ev.host_email, ev.host_name)}
                            disabled={liveCtlBusy !== null}
                            className="flex items-center gap-1.5 px-3.5 py-2 bg-[#374151] hover:bg-[#1F2937] text-white rounded-full text-[11px] font-bold transition-colors disabled:opacity-50"
                            title="Suspend this host's live privileges — ends all their streams instantly and blocks future ones"
                            data-testid={`live-control-ban-${ev.id}`}
                          >
                            <Ban className="w-3.5 h-3.5" /> Ban Host
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>,
            document.body
          )}
          {liveCtlWatch && createPortal(<LiveViewer eventId={liveCtlWatch} currentUser={currentUser} onClose={() => { setLiveCtlWatch(null); loadLiveCtl(); }} />, document.body)}
          {clEvent && createPortal(<LiveViewer eventId={clEvent.id} currentUser={currentUser} onClose={() => setClEvent(null)} />, document.body)}
          <div className="flex items-center gap-2" data-testid="master-user-chip">
            <button
              onClick={onEditAvatar}
              data-testid="master-avatar-btn"
              title="Change my avatar"
              className="w-8 h-8 rounded-full bg-[#374151] border-2 border-[#FCE6E9] overflow-hidden flex items-center justify-center text-white text-xs font-black font-heading hover:border-[#E5A0BC] transition-colors"
            >
              {currentUser?.avatar ? (
                <img src={currentUser.avatar} alt={currentUser.fullName} className="w-full h-full object-cover" />
              ) : (
                currentUser?.fullName?.charAt(0) || "M"
              )}
            </button>
            <div className="hidden lg:block">
              <p className="text-xs font-extrabold leading-tight">{currentUser?.fullName}</p>
              <p className="text-[10px] font-bold text-[#9CA3AF] uppercase tracking-wide leading-tight">{currentUser?.position || "master"}</p>
            </div>
            <button
              onClick={onSignOut}
              data-testid="master-sign-out-btn"
              className="flex items-center gap-1.5 px-3 py-2 bg-white hover:bg-[#FCE6E9] border border-[#FCE6E9] rounded-full text-xs font-bold text-[#6B7280] transition-colors"
            >
              <LogOut className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Sign Out</span>
            </button>
          </div>
        </div>
      </header>

      {/* Mobile section drawer */}
      {drawerOpen && (
        <div className="fixed inset-0 z-50 lg:hidden" data-testid="dash-nav-drawer">
          <div className="absolute inset-0 bg-[#374151]/30 backdrop-blur-sm" onClick={() => setDrawerOpen(false)} />
          <div className="absolute inset-y-0 left-0 w-72 bg-white border-r border-[#FCE6E9] p-4 overflow-y-auto shadow-xl">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-extrabold font-heading">Dashboard Sections</p>
              <button onClick={() => setDrawerOpen(false)} data-testid="dash-nav-drawer-close" className="w-8 h-8 rounded-full flex items-center justify-center text-[#9CA3AF] hover:bg-[#FCE6E9] transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>
            <DashNav collapsed={false} active={activeNav} badges={navBadges} onNav={(item) => { if (item.target === "__daycare-ops__") { onDashboard(); setDrawerOpen(false); return; } if (item.target === "__community-live__") { setCommunityLiveOpen(true); setDrawerOpen(false); return; } setActiveNav(item.target); scrollToTarget(item.target); setDrawerOpen(false); }} />
          </div>
        </div>
      )}

      <div className="max-w-[110rem] mx-auto px-4 sm:px-6 lg:px-8 py-8 flex gap-6 items-start">
        {/* Collapsible section menu (desktop) */}
        <aside className={`hidden lg:block shrink-0 sticky top-[84px] self-start bg-white border border-[#FCE6E9] rounded-3xl p-3 shadow-sm transition-all max-h-[calc(100vh-100px)] overflow-y-auto ${navOpen ? "w-60" : "w-16"}`} data-testid="master-dash-sidebar">
          <button
            onClick={() => setNavOpen((o) => !o)}
            data-testid="dash-nav-collapse"
            title={navOpen ? "Collapse menu" : "Expand menu"}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 mb-2 rounded-xl hover:bg-[#FCE6E9] text-[10px] font-extrabold uppercase tracking-wider text-[#9CA3AF] transition-colors"
          >
            <Menu className="w-4 h-4" /> {navOpen && "Sections"}
          </button>
          <DashNav collapsed={!navOpen} active={activeNav} badges={navBadges} onExpand={() => setNavOpen(true)} onNav={(item) => { if (item.target === "__daycare-ops__") { onDashboard(); return; } if (item.target === "__community-live__") { setCommunityLiveOpen(true); return; } setActiveNav(item.target); scrollToTarget(item.target); }} />
        </aside>

        <main className="flex-1 min-w-0 space-y-8">
        <div className="animate-fade-up">
          <h1 className="text-3xl font-black font-heading tracking-tight">Master Dashboard</h1>
          <p className="text-sm text-[#9CA3AF] font-medium mt-1">
            Platform-wide oversight for NestTrack internal staff. Access is restricted to invited master accounts (Secretary, CFO, Accountants, Admins).
          </p>
        </div>

        {/* Global user directory: search, contact, profile cards, suspend/delete — pinned to the top */}
        <UserDirectory scope="master" currentUserEmail={currentUser?.email} />

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4" data-testid="master-stats">
          {cards.map((s) => (
            <button
              key={s.testId}
              onClick={() => setStatDetail(s.detail)}
              className={`${ccCard} p-4 text-left cursor-pointer hover:shadow-md hover:-translate-y-0.5 transition-all duration-200`}
              data-testid={s.testId}
            >
              <span className={`w-9 h-9 rounded-full ${s.wrap} flex items-center justify-center mb-3`}>
                <s.icon className={`w-4 h-4 ${s.color}`} />
              </span>
              <p className="text-2xl font-black font-heading">{s.value}</p>
              <p className="text-[10px] font-bold text-[#9CA3AF] uppercase tracking-wide">{s.label}</p>
            </button>
          ))}
        </div>

        {/* Go-live readiness: real facility, payments, email, mock-data purge status */}
        <div id="sec-golive" className="scroll-mt-24">
          <GoLiveChecklist />
        </div>

        <StatDetailModal detail={statDetail} onClose={() => setStatDetail(null)} onSelect={(r) => { setStatDetail(null); setRecordReview(r); }} stats={data?.stats} facilities={data?.facilities} />
        <MasterRecordReview review={recordReview} onClose={() => setRecordReview(null)} onChanged={load} currentUserEmail={currentUser?.email} />

        {/* Onboarding: applications pipeline, facility setup, enrollment */}
        <div id="sec-onboarding" className="scroll-mt-24">
          <MasterOnboarding onOpenOwnerModal={onOpenOwnerModal} onOpenEnrollModal={onOpenEnrollModal} onReviewAccount={(email) => setRecordReview({ type: "account", email })} />
        </div>

        {/* Admin hierarchy: job position builder + regional scope assignment */}
        <div id="sec-hierarchy" className="scroll-mt-24">
          <MasterHierarchy staff={data?.staff || []} onChanged={load} />
        </div>

        {/* Support queue: contact-form inquiries */}
        <div id="sec-support" className="scroll-mt-24">
          <SupportInquiries />
        </div>

        {/* Store manager: catalog, categories, promos — requires the position's Moderate Store permission */}
        <div id="sec-store" className="scroll-mt-24">
          {canDo("can_moderate_store") && <StoreAdmin currentUser={currentUser} />}
        </div>

        {/* Real-time platform alerts + complaints oversight */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div id="sec-alerts" className="scroll-mt-24">
            <PlatformAlerts />
          </div>
          <div id="sec-complaints" className="scroll-mt-24">
            <ComplaintsOversight />
          </div>
        </div>

        {/* Broadcast engine: pop-up announcements to users */}
        <div id="sec-broadcast" className="scroll-mt-24">
          <BroadcastCenter />
        </div>
        <div id="sec-commlog" className="scroll-mt-24">
          <CommLog />
        </div>

        {/* Sponsor ads: placements, click tracking, PDF reports */}
        <div id="sec-ads" className="scroll-mt-24">
          <MasterAds />
        </div>

        {/* Featured daycares: curated landing-scroll listings + listing requests */}
        <div id="sec-featured" className="scroll-mt-24">
          <MasterFeatured />
        </div>

        {/* Site content: master-editable copy + per-page SEO meta */}
        <SiteContentEditor />

        {/* Visitor analytics + activity monitor + calendar + global search */}
        <div id="sec-analytics" className="scroll-mt-24">
          <MasterAnalytics onReviewAccount={(email) => setRecordReview({ type: "account", email })} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Invite people & master staff */}
          <div className={`${ccCard} p-6`} data-testid="master-invite-panel">
            <h3 className="text-base font-extrabold font-heading flex items-center gap-2.5 mb-1">
              <span className="w-9 h-9 rounded-full bg-[#FCE6E9] flex items-center justify-center">
                <UserPlus className="w-4 h-4 text-[#E5A0BC]" />
              </span>
              Invite People
            </h3>
            <p className="text-[11px] text-[#9CA3AF] font-medium mb-4">
              Invite master staff, caregivers, daycare owners, or parents — each gets an email with a single-use link to create their profile.
            </p>
            <form onSubmit={invite} className="space-y-3">
              <div>
                <label className={ccLabel}>Role to Invite</label>
                <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className={ccInput} data-testid="invite-role-select">
                  <option value="master">Master Staff (platform team)</option>
                  <option value="caregiver">Caregiver (Care Connections)</option>
                  <option value="daycare_admin">Daycare / Facility Owner</option>
                  <option value="parent">Parent</option>
                </select>
              </div>
              <div>
                <label className={ccLabel}>Full Name</label>
                <input required type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={ccInput} data-testid="invite-name-input" />
              </div>
              <div>
                <label className={ccLabel}>Email</label>
                <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={ccInput} data-testid="invite-email-input" />
              </div>
              {form.role === "master" && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={ccLabel}>Position</label>
                    <select value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })} className={ccInput} data-testid="invite-position-select">
                      {POSITIONS.map((p) => (
                        <option key={p.value} value={p.value}>
                          {p.label}
                        </option>
                      ))}
                      {customPositions.map((p) => (
                        <option key={p.id} value={p.title}>
                          {p.title}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className={ccLabel}>Region (state, optional)</label>
                    <input value={form.region || ""} onChange={(e) => setForm({ ...form, region: e.target.value })} placeholder="e.g. CA — regional admin" maxLength={2} className={ccInput} data-testid="invite-region-input" />
                  </div>
                  <div className="flex items-end">
                    <p className="text-[10px] font-bold text-[#9CA3AF] leading-snug pb-2.5">A random temporary password is generated and emailed with a set-your-own-password link.</p>
                  </div>
                </div>
              )}
              <button type="submit" disabled={inviting} className={`${ccBtnPink} disabled:opacity-50`} data-testid="invite-submit-btn">
                {inviting ? "Inviting..." : form.role === "master" ? "Send Master Invite" : `Send ${ { caregiver: "Caregiver", daycare_admin: "Daycare", parent: "Parent" }[form.role] } Invite`}
              </button>
            </form>
            {lastInvite && (
              <div className="mt-4 p-3.5 bg-[#E2F4E1] border border-[#C4E5C2] rounded-2xl" data-testid="invite-result">
                <p className="text-[11px] font-extrabold text-[#2F6031]">
                  {lastInvite.emailSent === false
                    ? `Email is rate-limited right now — copy the claim link below and share it manually with ${lastInvite.email}.`
                    : `Invite emailed to ${lastInvite.email}`}
                </p>
                <p className="text-[11px] text-[#2F6031] font-medium mt-0.5">
                  {lastInvite.role === "master" ? "Position" : "Role"}: {lastInvite.position} · Temporary password: <code className="font-black">{lastInvite.temp}</code>
                </p>
                {lastInvite.link && (
                  <button
                    type="button"
                    onClick={() => {
                      navigator.clipboard?.writeText(lastInvite.link).then(() => toast.success("Profile-creation link copied — share it anywhere."));
                    }}
                    className="mt-2 px-3 py-1.5 bg-white border border-[#C4E5C2] rounded-full text-[10px] font-extrabold text-[#2F6031] hover:bg-[#E2F4E1] transition-colors"
                    data-testid="invite-copy-link"
                  >
                    Copy profile-creation link
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Master staff list */}
          <div className={`${ccCard} p-6`} data-testid="master-staff-panel">
            <h3 className="text-base font-extrabold font-heading flex items-center gap-2.5 mb-4">
              <span className="w-9 h-9 rounded-full bg-[#E6F2FA] flex items-center justify-center">
                <ShieldCheck className="w-4 h-4 text-[#5CB3DC]" />
              </span>
              Master Staff ({data?.staff?.length ?? 0})
            </h3>
            <div className="space-y-2.5">
              {(data?.staff || []).map((s) => (
                <button key={s.id} onClick={() => setRecordReview({ type: "account", email: s.email })} className="w-full text-left p-3.5 bg-[#FFF9F2] border border-[#FCE6E9] rounded-2xl flex items-center justify-between gap-3 hover:bg-[#E6F2FA]/40 hover:border-[#B3E0F2] cursor-pointer transition-colors" data-testid={`master-staff-row-${s.email}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-[#374151] flex items-center justify-center text-white text-xs font-black font-heading">
                      {s.name.charAt(0)}
                    </div>
                    <div>
                      <p className="text-xs font-extrabold">{s.name}</p>
                      <p className="text-[11px] text-[#9CA3AF]">{s.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <span
                      role="button"
                      tabIndex={0}
                      title={s.live_public_approved ? "Approved to host PUBLIC live events — click to revoke" : "Not approved for public live events — click to approve"}
                      onClick={async (e) => {
                        e.stopPropagation();
                        const res = await fetch(`${API}/api/master/staff/${s.id}`, {
                          method: "PATCH",
                          headers: { "Content-Type": "application/json" },
                          credentials: "include",
                          body: JSON.stringify({ live_public_approved: !s.live_public_approved }),
                        });
                        if (res.ok) {
                          toast.success(`${s.name} ${s.live_public_approved ? "can no longer" : "can now"} host public live events.`);
                          load();
                        } else {
                          toast.error("Could not update public-live approval.");
                        }
                      }}
                      className={`px-2.5 py-1 border text-[10px] font-extrabold uppercase rounded-full cursor-pointer transition-colors ${
                        s.live_public_approved
                          ? "bg-[#E2F4E1] border-[#C4E5C2] text-[#2F6031]"
                          : "bg-white border-[#FCE6E9] text-[#9CA3AF] hover:border-rose-200 hover:text-rose-400"
                      }`}
                      data-testid={`master-staff-publiclive-${s.email}`}
                    >
                      {s.live_public_approved ? "Live: Public" : "Live: Private"}
                    </span>
                    {s.status === "suspended" && (
                      <span className="px-2.5 py-1 bg-rose-50 border border-rose-200 text-rose-500 text-[10px] font-extrabold uppercase rounded-full" data-testid={`master-staff-suspended-${s.email}`}>
                        suspended
                      </span>
                    )}
                    <span className="px-2.5 py-1 bg-[#E6F2FA] border border-[#B3E0F2] text-[#3D7E9E] text-[10px] font-extrabold uppercase rounded-full" data-testid={`master-staff-position-${s.email}`}>
                      {s.position}
                    </span>
                    {s.scope_tier && s.scope_tier !== "national" && (
                      <span className="px-2.5 py-1 bg-[#FFF2D1] border border-[#F5E3B3] text-[#A08244] text-[10px] font-extrabold uppercase rounded-full" data-testid={`master-staff-scope-${s.email}`}>
                        {s.scope_tier}
                      </span>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
        </main>
      </div>
    </div>
  );
}
